import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import ZendeskHelp from '@/shared/components/ZendeskHelp';

import { injectStateService, StateService } from '@/shared/services/StateService';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import SimplePanel from '@/shared/components/SimplePanel';
import Axios from 'axios';
import api from '@/shared/api';
import config from '@/config';
import moment from 'moment';
import dateFormatService from '@/shared/services/DateFormatService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import messages from '../taskMessages';
import PercentagesDashboard from './PercentagesDashboard';
import QuantitiesDashboard from './QuantitiesDashboard';
import TaskDashboardFilters, { defaultFilters } from './TaskDashboardFilters';
import { showWhenPermission } from '@/shared/components/security';
import { functionalities, privileges } from '@/shared/services/AuthService';

const sessionStorageKey = 'TaskDashboardParams';

class TaskDashboardContainer extends Component {
  state = {
    data: null,
    showPercentages: false,
    filters: {
      ...defaultFilters,
    },
  };

  constructor(props) {
    super(props);

    const { showPercentages = false, filters = null } =
      JSON.parse(sessionStorage.getItem(sessionStorageKey)) || {};
    this.state.showPercentages = showPercentages;
    if (filters) {
      if (filters.deadlineFrom) filters.deadlineFrom = moment.utc(filters.deadlineFrom);
      if (filters.deadlineUntil) filters.deadlineUntil = moment.utc(filters.deadlineUntil);
      this.state.filters = {
        ...defaultFilters,
        ...filters,
      };
    }
  }

  static propTypes = {
    intl: intlShape.isRequired,
    stateService: PropTypes.instanceOf(StateService).isRequired,
  };

  componentDidMount() {
    this.loadData();
  }

  componentWillUnmount() {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  loadData = () => {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();
    const { filters } = this.state;

    return api.base
      .post(`${config.appUrls.tasks}/api/dashboard/TaskProgressOverview`, filters, {
        cancelToken: this.cancelToken.token,
      })
      .then(({ data }) => {
        const { intl } = this.props;
        this.setState({
          data: data
            .map(x => {
              const templateName = intl.formatMessage(
                listItemMsg('tasktemplate', x.templateId, x.templateName),
              );
              const periodicityName = intl.formatMessage(
                x.periodicityId
                  ? listItemMsg('taskperiodicity', x.periodicityId, x.periodicityName)
                  : messages.NoPeriodicity,
              );
              const momentPeriod = moment(x.period, 'DD/MM/YYYY');
              const formattedDate = momentPeriod.isValid()
                ? dateFormatService.formatDate(moment(x.period, 'DD/MM/YYYY'))
                : x.period;
              return {
                ...x,
                label: `${templateName} - ${periodicityName} - ${formattedDate}`,
                sortValue: `${templateName} - ${periodicityName} - ${x.periodSort}`,
              };
            })
            .sort(dynamicSort('sortValue')),
        });

        // do something
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  toggleDashboards = showPercentages => {
    this.setState({ showPercentages });
    this.updateSessionStorage({ showPercentages });
  };

  updateSessionStorage = (value, replace = false) => {
    const currentValue = JSON.parse(sessionStorage.getItem(sessionStorageKey)) || {};
    const newValue = replace ? value : { ...currentValue, ...value };
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(newValue));
  };

  filtersChanged = filters => {
    const { filters: currentFilters } = this.state;

    if (JSON.stringify(filters) === JSON.stringify(currentFilters)) return;
    this.updateSessionStorage({ filters });

    this.setState({ filters, data: null }, () => this.loadData());
  };
  openOverview = p => {
    const { stateService } = this.props;
    const { filters } = this.state;
    const { userId, username, deadlineFrom, deadlineUntil, period, ownCompanies, teams } = filters;

    stateService.gotoState('taskflow.overview', {
      filters: { userId, username, deadlineFrom, deadlineUntil, period, ownCompanies, teams, ...p },
    });
  };

  render() {
    const { data, showPercentages, filters } = this.state;
    const { intl } = this.props;

    return (
      <SimplePanel article="5434833387154" title={intl.formatMessage(messages.TaskDashboard)}>
        <>
          <TaskDashboardFilters
            filters={filters}
            onFilterChange={x => this.filtersChanged(x)}
            showPercentages={showPercentages}
            onChartTypeChange={x => this.toggleDashboards(x)}
          />
          {data ? (
            <div
              className="chart-container"
              // style={{
              //   position: 'relative',
              //   width: '100%',
              //   minHeight: '150px',
              //   height: `${data.length * 5 + 100}px`,
              // }}
            >
              {showPercentages ? (
                <PercentagesDashboard data={data} onOpenOverview={this.openOverview} />
              ) : (
                <QuantitiesDashboard data={data} onOpenOverview={this.openOverview} />
              )}
            </div>
          ) : (
            <LoadingAnimation />
          )}
        </>
      </SimplePanel>
    );
  }
}

export default showWhenPermission(
  functionalities.PlannedTask,
  privileges.Read,
)(injectStateService(injectIntl(TaskDashboardContainer)));
